import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';

// Public pages
import Home from './pages/Home';
import CompanyInfo from './pages/CompanyInfo';
import Products from './pages/Products';
import ProductCategory from './pages/ProductCategory';
import CategoryBrandProducts from './pages/CategoryBrandProducts';
import ProductDetail from './pages/ProductDetail';
import Brands from './pages/Brands';
import BrandCatalog from './pages/BrandCatalog';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Careers from './pages/Careers';
import JobDetail from './pages/JobDetail';
import Promotions from './pages/Promotions';
import PromotionDetail from './pages/PromotionDetail';
import Contact from './pages/Contact';

// Admin pages
import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import AdminProducts from './admin/AdminProducts';
import AdminCategories from './admin/AdminCategories';
import AdminClients from './admin/AdminClients';
import AdminBrands from './admin/AdminBrands';
import AdminCareers from './admin/AdminCareers';
import AdminPromotions from './admin/AdminPromotions';
import AdminHero from './admin/AdminHero';
import AdminSettings from './admin/AdminSettings';
import AdminMessages from './admin/AdminMessages';
import AdminHelp from './admin/AdminHelp';
import { IS_ADMIN_HOST, ADMIN_BASE } from './utils/adminBase';

function ProtectedRoute({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  return admin ? children : <Navigate to={`${ADMIN_BASE}/login`} replace />;
}

// Shared by both mount points below — the admin subdomain mounts these at
// "/", the main domain mounts them at "/admin".
const adminChildRoutes = (
  <>
    <Route index element={<AdminDashboard />} />
    <Route path="hero" element={<AdminHero />} />
    <Route path="products" element={<AdminProducts />} />
    <Route path="categories" element={<AdminCategories />} />
    <Route path="clients" element={<AdminClients />} />
    <Route path="brands" element={<AdminBrands />} />
    <Route path="careers" element={<AdminCareers />} />
    <Route path="promotions" element={<AdminPromotions />} />
    <Route path="settings" element={<AdminSettings />} />
    <Route path="messages" element={<AdminMessages />} />
    <Route path="help" element={<AdminHelp />} />
  </>
);

export default function App() {
  return (
    <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {IS_ADMIN_HOST ? (
            <>
              {/* admin.<domain> serves only the admin panel, mounted at the root */}
              <Route path="/login" element={<AdminLogin />} />
              <Route path="/" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                {adminChildRoutes}
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            <>
              {/* Public */}
              <Route path="/" element={<Home />} />
              <Route path="/company-info" element={<CompanyInfo />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/:categorySlug" element={<ProductCategory />} />
              <Route path="/products/:categorySlug/:brandSlug" element={<CategoryBrandProducts />} />
              <Route path="/products/:categorySlug/:brandSlug/:productSlug" element={<ProductDetail />} />
              <Route path="/brands" element={<Brands />} />
              {/* Brand → its categories → sub categories → contact */}
              <Route path="/brands/:slug" element={<BrandCatalog />} />
              <Route path="/brands/:slug/:categorySlug" element={<BrandCatalog />} />
              <Route path="/brands/:slug/:categorySlug/:subSlug" element={<BrandCatalog />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/clients/:slug" element={<ClientDetail />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/careers/:slug" element={<JobDetail />} />
              <Route path="/promotional" element={<Promotions />} />
              <Route path="/promotional/:slug" element={<PromotionDetail />} />
              <Route path="/contact" element={<Contact />} />

              {/* Admin — also still reachable at /admin on the main domain */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                {adminChildRoutes}
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </LanguageProvider>
  );
}
